import { Tag } from '@/components/badges';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BuildArtifacts } from './BuildArtifacts';
import { BuildChecks } from './BuildChecks';
import { BuildEnvSnapshot } from './BuildEnvSnapshot';
import { BuildTimeline } from './BuildTimeline';
import { checksBadge } from './tabsModel';
import type { BuildTabQueries } from './useBuildTabQueries';

interface Props {
  tab: string;
  onTabChange: (tab: string) => void;
  queries: BuildTabQueries;
  now: number;
  /** The build has not reached a terminal status: empty checks and artifacts mean "not yet". */
  pending: boolean;
  /** The build service the build runs on (`eas`, `bitrise`), named in the timeline's status labels. */
}

/*
 * Phone: tighter triggers so the four tabs fit one row at 375 px (malible-premium-ui §2.7); 44 px tall from the registry.
 * Same variant chain as the registry's line tabs, so these win over its `px-2` / `gap-1.5`.
 */
const TRIGGER = 'max-sm:group-data-[variant=line]/tabs-list:gap-1 max-sm:group-data-[variant=line]/tabs-list:px-1 max-sm:text-[13px]';
/* The counts get narrower on a phone; the timeline's (the least useful) hides there. */
const COUNT = 'max-sm:px-1';

/** Timeline, checks, artifacts and env snapshot of a build. */
export function BuildTabs({ tab, onTabChange, queries, now, pending }: Props) {
  return (
    <Tabs value={tab} onValueChange={onTabChange}>
      <TabsList variant="line" className="px-2 max-sm:gap-0 max-sm:px-1" aria-label="Build sections">
        <TabTriggers queries={queries} />
      </TabsList>
      <TabPanels queries={queries} now={now} pending={pending} />
    </Tabs>
  );
}

function TabTriggers({ queries: { events, checks, artifacts } }: { queries: BuildTabQueries }) {
  const checksCount = checks.data ? checksBadge(checks.data) : null;
  return (
    <>
      <TabsTrigger value="timeline" data-testid="build-tab-timeline" className={TRIGGER}>
        Timeline
        {events.data ? <Tag className="max-sm:hidden">{events.data.length}</Tag> : null}
      </TabsTrigger>
      <TabsTrigger value="checks" data-testid="build-tab-checks" className={TRIGGER}>
        Checks
        {checksCount ? (
          <Tag className={COUNT} title="Checks passed out of all checks">
            {checksCount}
          </Tag>
        ) : null}
      </TabsTrigger>
      <TabsTrigger value="artifacts" data-testid="build-tab-artifacts" className={TRIGGER}>
        Artifacts
        {artifacts.data && artifacts.data.length > 0 ? <Tag className={COUNT}>{artifacts.data.length}</Tag> : null}
      </TabsTrigger>
      <TabsTrigger value="env" data-testid="build-tab-env" className={TRIGGER} title="The environment variables this build used">
        Variables
      </TabsTrigger>
    </>
  );
}

/* `isPending` (no data yet, not failed): a tab paints its skeleton before the first fetch starts, never its empty copy. */
function TabPanels({ queries: { events, checks, artifacts, env }, now, pending }: { queries: BuildTabQueries; now: number; pending: boolean }) {
  return (
    <>
      <TabsContent value="timeline">
        <BuildTimeline events={events.data} isLoading={events.isPending} error={events.error} onRetry={() => void events.refetch()} now={now} />
      </TabsContent>
      <TabsContent value="checks">
        <BuildChecks checks={checks.data} isLoading={checks.isPending} error={checks.error} onRetry={() => void checks.refetch()} pending={pending} />
      </TabsContent>
      <TabsContent value="artifacts">
        <BuildArtifacts artifacts={artifacts.data} isLoading={artifacts.isPending} error={artifacts.error} onRetry={() => void artifacts.refetch()} pending={pending} />
      </TabsContent>
      <TabsContent value="env">
        <BuildEnvSnapshot snapshot={env.data} isLoading={env.isPending} error={env.error} onRetry={() => void env.refetch()} />
      </TabsContent>
    </>
  );
}
